import { Button, Select, TextInput } from '@mantine/core';
import { IconCalendar, IconPlus, IconSearch } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { useFitColumns } from '../../fit-columns.js';
import { formatDay, formatTime } from '../../format.js';
import { DayInput } from '../../inputs/DayInput.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import table from '../materials/Materials.module.css';
import { errorCode } from '../personnel/source.js';
import type { PersonnelVessel } from '../personnel/types.js';
import { useCountryName } from '../ports/catalog-context.js';
import { sortItems } from '../purchasing/requisitions.js';
import {
  LoadError,
  NoMatch,
  SortableTh,
  type SortState,
  TablePager,
  TableSkeleton,
} from '../purchasing/table.js';
import { PortCallStatusBadge } from './badges.js';
import classes from './PortCalls.module.css';
import {
  callDay,
  durationText,
  filterPortCalls,
  NO_PORT_CALL_FILTER,
  type PortCallFilter,
  STATUS_TABS,
  statusCounts,
} from './port-calls.js';
import type { PortCallsSource } from './source.js';
import type { PortCallList as List, PortCall } from './types.js';

type Column = 'date' | 'vessel' | 'port' | 'country' | 'type' | 'duration' | 'status';

const SORT_VALUE: Record<Column, (item: PortCall) => string | number | null> = {
  date: (item) => item.ata ?? item.eta,
  vessel: (item) => item.vesselName,
  port: (item) => item.portName,
  country: (item) => item.country,
  type: (item) => item.locationType,
  duration: (item) => item.durationHours,
  status: (item) => item.status,
};

const PAGE_SIZE = 10;

const DROP: Partial<Record<Column, number>> = { country: 2, type: 4 };

type State =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; list: List; vessels: PersonnelVessel[] };

/**
 * Port Calls listesi (BGM `gemi-operasyon.jpeg` "5. Port Calls"; ortağın `ekran-3.jpeg` "Port Calls
 * List"): "New Port Call", Vessel / arama / tarih aralığı süzgeçleri, durum sekmeleri (sayılarla);
 * Date (ETA/ETD) / Vessel / Port / Country / Type / Activities / Agent / Duration / Status
 * tablosu, sıralama ve sayfalama. Gemide yalnız o gemi; ofiste filo, salt okunur.
 */
export function PortCallList({
  source,
  onOpen,
  onNew,
}: {
  source: PortCallsSource;
  onOpen: (id: string) => void;
  onNew: () => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`portCalls.list.${key}`, options);
  const countryName = useCountryName();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [filter, setFilter] = useState<PortCallFilter>(NO_PORT_CALL_FILTER);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortState<Column>>({ key: 'date', dir: 'asc' });
  // Dar tabloda önce acente, ülke, faaliyetler, tür, süre gizlenir (`fit-columns.ts`); BGM'nin
  // sütunları (tarih, gemi, liman, tür, süre, durum) en son
  const tableBox = useFitColumns<HTMLElement>();

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const [list, vessels] = await Promise.all([source.list(), source.vessels()]);
      setState({ kind: 'ready', list, vessels });
    } catch (error) {
      setState({ kind: 'error', code: errorCode(error) });
    }
  }, [source]);
  useEffect(() => {
    void load();
  }, [load]);

  const ready = state.kind === 'ready' ? state : null;
  const items = useMemo(
    () =>
      ready
        ? sortItems(filterPortCalls(ready.list.items, filter), sort.dir, SORT_VALUE[sort.key])
        : [],
    [ready, filter, sort],
  );
  const counts = useMemo(() => statusCounts(ready?.list.items ?? [], filter), [ready, filter]);
  const pages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const visible = items.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const change = (next: Partial<PortCallFilter>) => {
    setFilter((f) => ({ ...f, ...next }));
    setPage(1);
  };
  const ship = source.scope === 'ship';
  const canCreate = Boolean(ready?.list.can.create && source.editor);
  const unit = { day: t('portCalls.units.day'), hour: t('portCalls.units.hour') };

  return (
    <div className={classes.screen}>
      <PageHeader title={k('title')} description={k(ship ? 'descriptionShip' : 'descriptionWeb')} />

      <Panel flush aria-label={k('table')}>
        <div className={classes.toolbar} data-single={canCreate ? undefined : true}>
          {canCreate ? (
            <Button leftSection={<IconPlus size={16} />} onClick={onNew}>
              {k('new')}
            </Button>
          ) : null}
          <form
            className={classes.filters}
            aria-label={k('filters')}
            onSubmit={(event) => event.preventDefault()}
          >
            <Select
              label={k('vessel')}
              data={[
                { value: '', label: k('allVessels') },
                ...(ready?.vessels ?? []).map((v) => ({ value: v.id, label: v.name })),
              ]}
              value={ship ? (ready?.vessels[0]?.id ?? '') : (filter.vesselId ?? '')}
              allowDeselect={false}
              readOnly={ship}
              {...(ship ? { rightSection: null } : {})}
              onChange={(value) => change({ vesselId: value || null })}
            />
            <TextInput
              label={k('search')}
              placeholder={k('searchPlaceholder')}
              leftSection={<IconSearch size={15} aria-hidden />}
              value={filter.query}
              onChange={(event) => change({ query: event.currentTarget.value })}
            />
            <fieldset className={classes.range}>
              <legend className={classes.label}>
                {k('dateRange')} ({k('dateRangePlaceholder')})
              </legend>
              <div className={classes.rangeBox}>
                <IconCalendar size={16} aria-hidden />
                <DayInput
                  variant="unstyled"
                  size="xs"
                  rightSection={null}
                  aria-label={k('dateFrom')}
                  value={filter.from}
                  maxDate={filter.to ?? undefined}
                  onChange={(value) => change({ from: value })}
                />
                <span aria-hidden>–</span>
                <DayInput
                  variant="unstyled"
                  size="xs"
                  rightSection={null}
                  aria-label={k('dateTo')}
                  value={filter.to}
                  minDate={filter.from ?? undefined}
                  onChange={(value) => change({ to: value })}
                />
              </div>
            </fieldset>
          </form>
        </div>
        <fieldset className={classes.tabs} aria-label={k('statusTabs')}>
          {STATUS_TABS.map((tab) => (
            <button
              key={tab}
              type="button"
              className={classes.tab}
              aria-pressed={filter.status === tab}
              onClick={() => change({ status: tab })}
            >
              {t(`portCalls.tabs.${tab}`)}
              <span className={classes.count}>{counts[tab]}</span>
            </button>
          ))}
        </fieldset>
        {state.kind === 'error' ? (
          <LoadError title={k('error')} code={state.code} onRetry={() => void load()} />
        ) : state.kind === 'loading' ? (
          <TableSkeleton columns={10} label={k('table')} />
        ) : (ready?.list.items.length ?? 0) === 0 ? (
          <EmptyState
            title={k('emptyTitle')}
            description={k(canCreate ? 'emptyShip' : 'emptyWeb')}
          />
        ) : items.length === 0 ? (
          <NoMatch
            title={k('noMatch')}
            onClear={() => {
              setFilter(NO_PORT_CALL_FILTER);
              setPage(1);
            }}
          />
        ) : (
          <>
            <section
              ref={tableBox}
              className={table.tableWrap}
              aria-label={k('table')}
              // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
              tabIndex={0}
            >
              <table className={`${table.table} ${classes.table}`}>
                <thead>
                  <tr>
                    <th scope="col" className={classes.index}>
                      {k('columns.no')}
                    </th>
                    {(['date', 'vessel', 'port', 'country', 'type'] as const).map((column) => (
                      <SortableTh
                        key={column}
                        column={column}
                        label={k(`columns.${column}`)}
                        sort={sort}
                        onSort={setSort}
                        drop={DROP[column]}
                        {...(column === 'date' ? { className: classes.wrapTh } : {})}
                      />
                    ))}
                    <th scope="col" data-drop={3}>
                      {k('columns.activities')}
                    </th>
                    <th scope="col" data-drop={1}>
                      {k('columns.agent')}
                    </th>
                    <SortableTh
                      column="duration"
                      label={k('columns.duration')}
                      sort={sort}
                      onSort={setSort}
                      drop={5}
                    />
                    <SortableTh
                      column="status"
                      label={k('columns.status')}
                      sort={sort}
                      onSort={setSort}
                    />
                  </tr>
                </thead>
                <tbody>
                  {visible.map((item, index) => (
                    <tr key={item.id}>
                      <td className={classes.index}>{(current - 1) * PAGE_SIZE + index + 1}</td>
                      <td>
                        <span className={classes.stamp}>
                          <span>{formatDay(callDay(item))}</span>
                          <small>
                            {formatTime(item.eta, item.tzOffsetMin)} /{' '}
                            {formatTime(item.etd, item.tzOffsetMin)}
                          </small>
                        </span>
                      </td>
                      <td>{item.vesselName}</td>
                      <td>
                        <a
                          className={classes.link}
                          href={`?id=${item.id}`}
                          aria-label={k('open', { port: item.portName })}
                          onClick={(event) => {
                            event.preventDefault();
                            onOpen(item.id);
                          }}
                        >
                          {item.portName}
                        </a>
                        {item.voyageNo ? (
                          <span className={classes.sub}>{item.voyageNo}</span>
                        ) : null}
                      </td>
                      <td data-drop={2}>{countryName(item.country)}</td>
                      <td data-drop={4}>{t(`portCalls.locationTypes.${item.locationType}`)}</td>
                      <td className={classes.activities} data-drop={3}>
                        {item.activities.length > 0
                          ? item.activities.map((a) => t(`portCalls.activities.${a}`)).join(', ')
                          : '—'}
                      </td>
                      <td className={classes.agent} data-drop={1}>
                        {item.agentName ?? '—'}
                      </td>
                      <td data-drop={5}>
                        {durationText(item.durationHours, unit)}
                        {item.durationActual ? null : (
                          <span className={classes.sub}>{k('estimated')}</span>
                        )}
                      </td>
                      <td>
                        <PortCallStatusBadge status={item.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
            <TablePager
              page={current}
              pages={pages}
              summary={k('showing', {
                from: (current - 1) * PAGE_SIZE + 1,
                to: Math.min(current * PAGE_SIZE, items.length),
                total: items.length,
              })}
              label={k('pagination')}
              onPage={setPage}
            />
          </>
        )}
      </Panel>
    </div>
  );
}
